import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Scale, ShieldCheck, FileText, Receipt, Languages } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LangPill } from "@/components/LangPill";
import { LEGAL_CONTENT } from "@/lib/legalContent";

/**
 * ConditionsPage — public legal page (FR original / EN courtesy translation)
 * with four tabs: legal notice, terms of use, terms of sale, privacy policy.
 * Hash-aware so share links (/conditions#confidentialite) land on the right tab.
 */
export default function ConditionsPage() {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("fr") ? "fr" : "en";
  const c = LEGAL_CONTENT[lang];
  const [tab, setTab] = useState("mentions-legales");

  useEffect(() => {
    if (hash === "#confidentialite") setTab("confidentialite");
    else if (hash === "#cgu") setTab("cgu");
    else if (hash === "#cgv") setTab("cgv");
    else if (hash === "#mentions-legales" || hash === "") setTab("mentions-legales");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);

  function onTabChange(v) {
    setTab(v);
    navigate({ hash: `#${v}` }, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const tabCls =
    "flex items-center gap-2 py-3 rounded-xl data-[state=active]:shadow-sm data-[state=active]:bg-background";

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="conditions-page" lang={lang}>
      <div className="max-w-3xl mx-auto px-6 py-10 sm:py-14">
        <div className="flex items-center justify-between gap-4">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            data-testid="conditions-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            {c.back}
          </Link>
          <LangPill className="border-border bg-muted/40" idPrefix="conditions-lang" />
        </div>

        <div className="mt-8 mb-8 text-center">
          <div className="text-[11px] uppercase tracking-[0.34em] font-bold text-muted-foreground">
            {c.kicker}
          </div>
          <h1 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight" data-testid="conditions-title">
            {c.title}
          </h1>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">{c.subtitle}</p>
        </div>

        {c.notice && (
          <div
            className="mb-6 flex items-start gap-3 rounded-2xl border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground"
            data-testid="conditions-translation-notice"
          >
            <Languages className="h-4 w-4 mt-0.5 shrink-0" />
            <p>{c.notice}</p>
          </div>
        )}

        <Tabs value={tab} onValueChange={onTabChange} className="w-full">
          <TabsList
            className="grid grid-cols-2 sm:grid-cols-4 w-full h-auto rounded-2xl p-1.5 bg-muted/40"
            data-testid="conditions-tabs"
          >
            <TabsTrigger value="mentions-legales" className={tabCls} data-testid="conditions-tab-legal">
              <Scale className="h-4 w-4" />
              <span className="font-semibold hidden lg:inline">{c.tabs.legal}</span>
              <span className="font-semibold lg:hidden">{c.tabs.legal_short}</span>
            </TabsTrigger>
            <TabsTrigger value="cgu" className={tabCls} data-testid="conditions-tab-cgu">
              <FileText className="h-4 w-4" />
              <span className="font-semibold">{c.tabs.cgu}</span>
            </TabsTrigger>
            <TabsTrigger value="cgv" className={tabCls} data-testid="conditions-tab-cgv">
              <Receipt className="h-4 w-4" />
              <span className="font-semibold">{c.tabs.cgv}</span>
            </TabsTrigger>
            <TabsTrigger value="confidentialite" className={tabCls} data-testid="conditions-tab-privacy">
              <ShieldCheck className="h-4 w-4" />
              <span className="font-semibold">{c.tabs.privacy}</span>
            </TabsTrigger>
          </TabsList>

          <LegalTab value="mentions-legales" content={c.legal} testid="conditions-content-mentions" />
          <LegalTab value="cgu" content={c.cgu} testid="conditions-content-legal" />
          <LegalTab value="cgv" content={c.cgv} testid="conditions-content-cgv" />
          <LegalTab value="confidentialite" content={c.privacy} testid="conditions-content-privacy" />
        </Tabs>
      </div>
    </div>
  );
}

function LegalTab({ value, content, testid }) {
  return (
    <TabsContent value={value} className="mt-8 qrise-card p-6 sm:p-10 space-y-6" data-testid={testid}>
      {content.kicker && (
        <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-muted-foreground">
          {content.kicker}
        </div>
      )}
      {content.sections.map((s) => (
        <LegalSection key={s.title} title={s.title}>
          {s.paragraphs.map((p, i) => (
            <p key={i} className={i > 0 ? "mt-3" : undefined} dangerouslySetInnerHTML={{ __html: p }} />
          ))}
          {s.list && (
            <ul className="mt-3 space-y-1.5">
              {s.list.map((li, i) => (
                <li key={i} dangerouslySetInnerHTML={{ __html: li }} />
              ))}
            </ul>
          )}
          {s.after?.map((p, i) => (
            <p key={`a${i}`} className="mt-3" dangerouslySetInnerHTML={{ __html: p }} />
          ))}
        </LegalSection>
      ))}
      {content.footer && (
        <p className="text-sm text-muted-foreground pt-4 border-t border-border/60">{content.footer}</p>
      )}
    </TabsContent>
  );
}

function LegalSection({ title, children }) {
  return (
    <section>
      <h2 className="text-lg sm:text-xl font-bold tracking-tight mb-2">{title}</h2>
      <div className="text-sm sm:text-[15px] text-muted-foreground leading-relaxed">{children}</div>
    </section>
  );
}
